import {
  PERMISSION_CANTILEVER_ACCESS_ADMIN,
  PERMISSION_CANTILEVER_ACCESS_EDITOR,
  PERMISSION_CANTILEVER_ACCESS_MEMBER,
  PERMISSION_CANTILEVER_ACCESS_VIEWER,
  type RolodexPermissionsValues,
} from 'types'
import { z } from 'zod'

export const RolodexUserAccess = {
  MEMBER: 0,
  VIEWER: 1,
  EDITOR: 2,
  ADMIN: 3,
} as const

export const rolodexV3ScopeSchema = z.object({
  type: z.string(),
  name: z.string(),
})
export type RolodexV3Scope = z.infer<typeof rolodexV3ScopeSchema>

export const rolodexV3UserScopeSchema = z.object({
  type: z.string(),
  name: z.string(),
  access: z.number().optional(),
})
export type RolodexV3UserScope = z.infer<typeof rolodexV3UserScopeSchema>

export const rolodexV3UserScopesResponseSchema = z.array(
  rolodexV3UserScopeSchema
)

export const ROOT_SCOPE_TYPE = 'root'
export const ROOT_SCOPE_NAME = 'all'
export const ROOT_TAGS: Record<string, string> = {
  [ROOT_SCOPE_TYPE]: ROOT_SCOPE_NAME,
}

/** Maps a User Scope onto tags the rest of Manage Access already evaluates. */
export function tagsFromUserScope(
  scope: RolodexV3UserScope
): Record<string, string> {
  if (scope.type === ROOT_SCOPE_TYPE) {
    return { [ROOT_SCOPE_TYPE]: scope.name }
  }
  if (scope.type === 'organization') {
    return { organization: scope.name }
  }
  if (scope.type === 'site') {
    return { site: scope.name }
  }
  if (scope.type === 'unit') {
    return { unit: scope.name }
  }
  return { [scope.type]: scope.name }
}

/** Inverse of tagsFromUserScope. Site wins over organization on dual-tag rows. */
export function scopeFromRoleTags(
  tags: Record<string, string> | undefined
): RolodexV3Scope {
  if (!tags || Object.keys(tags).length === 0) {
    return { type: ROOT_SCOPE_TYPE, name: ROOT_SCOPE_NAME }
  }
  if (tags[ROOT_SCOPE_TYPE]) {
    return { type: ROOT_SCOPE_TYPE, name: tags[ROOT_SCOPE_TYPE] }
  }
  if (tags.site) {
    return { type: 'site', name: tags.site }
  }
  if (tags.organization) {
    return { type: 'organization', name: tags.organization }
  }
  if (tags.unit) {
    return { type: 'unit', name: tags.unit }
  }
  const [[type, name]] = Object.entries(tags)
  return { type, name }
}

export function rolodexV3ScopeQueryParam(scope: RolodexV3Scope): string {
  return `${scope.type}:${scope.name}`
}

export function policyNameForAccess(access: number): string {
  switch (access) {
    case RolodexUserAccess.ADMIN:
      return PERMISSION_CANTILEVER_ACCESS_ADMIN
    case RolodexUserAccess.EDITOR:
      return PERMISSION_CANTILEVER_ACCESS_EDITOR
    case RolodexUserAccess.VIEWER:
      return PERMISSION_CANTILEVER_ACCESS_VIEWER
    default:
      return PERMISSION_CANTILEVER_ACCESS_MEMBER
  }
}

export function accessLevelOf(scope: RolodexV3UserScope): number {
  return scope.access ?? RolodexUserAccess.MEMBER
}

/** Manage Access picker values for the unscoped and org-wide options. */
export const UNSCOPED_SITE_ID = '__unscoped__'
export const ORG_SCOPE_ID = '__org__'

/**
 * The org or site a grant is checked against. `root` means no org is
 * selected, so only root grants apply.
 */
export type ActiveScope =
  | { kind: 'root' }
  | { kind: 'organization'; orgId: string }
  | { kind: 'site'; orgId: string | undefined; siteId: string }

export function activeScopeFor(
  orgId: string | undefined,
  siteId: string | undefined
): ActiveScope {
  if (siteId) {
    return { kind: 'site', orgId, siteId }
  }
  if (orgId) {
    return { kind: 'organization', orgId }
  }
  return { kind: 'root' }
}

/** Maps a Manage Access picker value to its scope; undefined when nothing is picked. */
export function activeScopeFromPicker(
  scopeId: string | undefined,
  orgId: string | undefined
): ActiveScope | undefined {
  if (!scopeId) {
    return undefined
  }
  if (scopeId === UNSCOPED_SITE_ID || !orgId) {
    return { kind: 'root' }
  }
  if (scopeId === ORG_SCOPE_ID) {
    return { kind: 'organization', orgId }
  }
  return { kind: 'site', orgId, siteId: scopeId }
}

/**
 * Root covers everything, an organization covers itself and its sites, and a
 * site covers only itself. Unit grants carry no site, so they cover nothing.
 */
export function scopeCovers(
  grant: RolodexV3Scope,
  active: ActiveScope
): boolean {
  switch (grant.type) {
    case ROOT_SCOPE_TYPE:
      return grant.name === ROOT_SCOPE_NAME
    case 'organization':
      return active.kind !== 'root' && grant.name === active.orgId
    case 'site':
      return active.kind === 'site' && grant.name === active.siteId
    default:
      return false
  }
}

/** The highest Manage Access level among the scopes covering `active`. */
export function accessLevelAt(
  scopes: readonly RolodexV3UserScope[],
  active: ActiveScope | undefined
): number | undefined {
  if (!active) {
    return undefined
  }
  const levels = scopes
    .filter((scope) => scopeCovers(scope, active))
    .map(accessLevelOf)
  return levels.length > 0 ? Math.max(...levels) : undefined
}

export const MANAGE_ACCESS_ROLE_PERMISSIONS: RolodexPermissionsValues[] = [
  PERMISSION_CANTILEVER_ACCESS_ADMIN,
  PERMISSION_CANTILEVER_ACCESS_EDITOR,
  PERMISSION_CANTILEVER_ACCESS_VIEWER,
]

export interface CantileverAccessScopeEvaluation {
  canOpenManageAccess: boolean
  hasUnscopedAccess: boolean
  hasOrgWideAccess: boolean
  allowedSiteIds: ReadonlySet<string>
  canSeeAllSites: boolean
  canSeeAllOrgs: boolean
}

/** v3 rejects assignments with no tags; empty tags mean no access, not unscoped. */
function isRootGrant(tags: Record<string, string> | undefined): boolean {
  return (
    tags?.[ROOT_SCOPE_TYPE] === ROOT_SCOPE_NAME &&
    !tags.organization &&
    !tags.site
  )
}

/** The unscoped picker matches root-tagged grants only. */
export function grantTagsMatchScope(
  tags: Record<string, string> | undefined,
  siteId: string | undefined,
  orgId: string | undefined
): boolean {
  const organization = tags?.organization
  const site = tags?.site

  if (siteId === UNSCOPED_SITE_ID) {
    return isRootGrant(tags)
  }
  if (!siteId || !orgId) {
    return false
  }
  if (siteId === ORG_SCOPE_ID) {
    return organization === orgId && !site
  }
  return site === siteId && (!organization || organization === orgId)
}

/** Roster rows match the selected picker exactly; covering ancestors do not inherit in. */
export function userBelongsInAccessView(
  scopes: readonly RolodexV3UserScope[] | undefined,
  siteId?: string,
  orgId?: string
): boolean {
  return (scopes ?? []).some((scope) =>
    grantTagsMatchScope(tagsFromUserScope(scope), siteId, orgId)
  )
}

export interface ManageAccessWriteCapabilities {
  canChangeUserTypes: boolean
  canChangeRoles: boolean
  canAdministerUsers: boolean
}

/**
 * At the picked scope, editors and admins may change user types and role
 * checkboxes, and admins may invite, assign sites, and deactivate.
 * Everything is off during an access preview.
 */
export function manageAccessWriteCapabilities(
  scopes: readonly RolodexV3UserScope[],
  pickerScopeId: string | undefined,
  orgId: string | undefined,
  accessPreview: boolean
): ManageAccessWriteCapabilities {
  const level = accessPreview
    ? undefined
    : accessLevelAt(scopes, activeScopeFromPicker(pickerScopeId, orgId))
  const canEditDropdowns =
    level !== undefined && level >= RolodexUserAccess.EDITOR
  return {
    canChangeUserTypes: canEditDropdowns,
    canChangeRoles: canEditDropdowns,
    canAdministerUsers: level !== undefined && level >= RolodexUserAccess.ADMIN,
  }
}

function canOpenManageAccessAtLevel(level: number | undefined): boolean {
  return level !== undefined && level >= RolodexUserAccess.VIEWER
}

/**
 * Whether the navbar offers Manage Access at the selected site. Without a
 * site, any admin, editor, or viewer grant counts. With a site, the grant must
 * cover it, so a member-only site gets no link.
 */
export function canOpenManageAccessAtSite(
  scopes: readonly RolodexV3UserScope[],
  siteId: string | undefined,
  orgId: string | undefined
): boolean {
  if (!siteId) {
    return scopes.some((scope) =>
      canOpenManageAccessAtLevel(accessLevelOf(scope))
    )
  }
  return canOpenManageAccessAtLevel(
    accessLevelAt(scopes, activeScopeFor(orgId, siteId))
  )
}

/**
 * Access admin, editor, or viewer may open Manage Access; member may not.
 * Outside a preview only v3 scopes count. A preview has only the previewed
 * user's role names, so it checks those.
 */
export function userCanSeeManageAccess(
  canOpenFromScopes: boolean,
  hasPermissions: (permissions: RolodexPermissionsValues[]) => boolean,
  accessPreview: boolean
): boolean {
  return accessPreview
    ? hasPermissions(MANAGE_ACCESS_ROLE_PERMISSIONS)
    : canOpenFromScopes
}

/** Derives Manage Access visibility and the scopes the user may pick from their v3 scopes. */
export function evaluateUserAccessScope(
  scopes: readonly RolodexV3UserScope[] = [],
  currentOrgId?: string
): CantileverAccessScopeEvaluation {
  let canOpenManageAccess = false
  let hasUnscopedAccess = false
  let hasOrgWideAccess = false
  const allowedSiteIds = new Set<string>()

  for (const scope of scopes) {
    if (!canOpenManageAccessAtLevel(accessLevelOf(scope))) {
      continue
    }
    canOpenManageAccess = true

    if (scope.type === ROOT_SCOPE_TYPE && scope.name === ROOT_SCOPE_NAME) {
      hasUnscopedAccess = true
      hasOrgWideAccess = true
      continue
    }
    if (!currentOrgId) {
      continue
    }
    if (scope.type === 'organization') {
      hasOrgWideAccess ||= scope.name === currentOrgId
    } else if (scope.type === 'site') {
      allowedSiteIds.add(scope.name)
    }
  }

  return {
    canOpenManageAccess,
    hasUnscopedAccess,
    hasOrgWideAccess,
    allowedSiteIds,
    canSeeAllSites: hasUnscopedAccess || hasOrgWideAccess,
    canSeeAllOrgs: hasUnscopedAccess,
  }
}
